import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import gsap from 'gsap';
import { hasPermission, type Permission } from '@deployctl/shared';
import { apiFetch } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';
import { ThemeToggle } from '../ui/ThemeToggle.js';
import './appshell.css';

interface NavItem {
  to: string;
  label: string;
  permission?: Permission;
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Deploy',
    items: [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/stacks', label: 'Stacks', permission: 'stacks:read' },
      { to: '/deployments', label: 'Deployments', permission: 'deploy:read' },
    ],
  },
  {
    section: 'Infrastructure',
    items: [
      { to: '/infrastructure/droplets', label: 'Droplets', permission: 'do:read' },
      { to: '/infrastructure/databases', label: 'Databases', permission: 'do:read' },
      { to: '/infrastructure/spaces', label: 'Spaces', permission: 'do:read' },
      { to: '/infrastructure/load-balancers', label: 'Load Balancers', permission: 'do:read' },
      { to: '/infrastructure/firewalls', label: 'Firewalls', permission: 'do:read' },
      { to: '/dns', label: 'DNS', permission: 'dns:read' },
    ],
  },
  {
    section: 'Admin',
    items: [
      { to: '/credentials', label: 'Credentials', permission: 'credentials:manage' },
      { to: '/admin/users', label: 'Users', permission: 'users:manage' },
      { to: '/admin/audit-log', label: 'Audit Log', permission: 'audit:read' },
      { to: '/admin/settings', label: 'Settings', permission: 'settings:manage' },
    ],
  },
];

export function AppShell() {
  const { data: me, isLoading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const mainRef = useRef<HTMLElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const [apiUp, setApiUp] = useState(true);

  useEffect(() => {
    const check = () => {
      fetch('/api/v1/health')
        .then((r) => setApiUp(r.ok))
        .catch(() => setApiUp(false));
    };
    check();
    const t = setInterval(check, 30_000);
    return () => clearInterval(t);
  }, []);

  // sidebar entrance, once
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      gsap.from('.shell-navgroup, .shell-brand, .shell-user', {
        opacity: 0,
        x: -14,
        duration: 0.5,
        stagger: 0.07,
        ease: 'power3.out',
      });
    }, sidebarRef);
    return () => ctx.revert();
  }, []);

  // content reveal on every route change
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!mainRef.current) return;
    const anim = gsap.fromTo(
      mainRef.current,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'all' },
    );
    return () => void anim.kill();
  }, [location.pathname]);

  if (isLoading) {
    return <div className="shell-loading mono">loading…</div>;
  }
  if (!me) return <Navigate to="/login" replace />;
  if (me.mfaPending) return <Navigate to="/login" replace />;
  if (!me.totpEnabled) return <Navigate to="/setup-2fa" replace />;

  const logout = async () => {
    await apiFetch('/auth/logout', { method: 'POST' });
    queryClient.clear();
    navigate('/login');
  };

  return (
    <div className="shell">
      <aside className="shell-sidebar" ref={sidebarRef}>
        <div className="shell-brand">
          <img className="shell-mark" src="/sherpa-logo.svg" alt="" aria-hidden="true" />
          <div>
            <div className="shell-name">
              Sherpa<em>Deploy</em>
            </div>
            <div className="shell-tag mono">Deployment Control</div>
          </div>
        </div>
        <nav>
          {NAV.map((group) => {
            const items = group.items.filter(
              (i) => !i.permission || hasPermission(me.role, i.permission),
            );
            if (items.length === 0) return null;
            return (
              <div key={group.section} className="shell-navgroup">
                <h2>{group.section}</h2>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => (isActive ? 'active' : '')}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="shell-status mono">
          <span className={`shell-status-dot ${apiUp ? '' : 'down'}`} />
          {apiUp ? 'ALL SYSTEMS OPERATIONAL' : 'CONTROL PLANE UNREACHABLE'}
        </div>
        <div className="shell-user">
          <NavLink to="/account" className="shell-username">
            <span>{me.displayName}</span>
            <span className="mono shell-role">{me.role}</span>
          </NavLink>
          <div className="shell-user-actions">
            <button className="btn btn-ghost shell-logout" onClick={logout}>
              Sign out
            </button>
            <ThemeToggle />
          </div>
        </div>
      </aside>
      <main className="shell-main" ref={mainRef}>
        <Outlet />
      </main>
    </div>
  );
}
